/**
 * DragGhost — subtle translucent representation following the pointer during drag.
 *
 * glance-grammar-spec §5.1 (Tile drag start):
 *   - Low opacity (0.5), no shadow or bounce — calm direct manipulation.
 *   - Shadow increase + slight scale-up (1.02×) per spec.
 *   - Target cell outline shown while dragging.
 *
 * §3.3 Invalid-drop spring revert:
 *   When dragState.phase === 'reverting', the ghost renders at revertFromX/Y and
 *   animates to revertToX/Y using --spring-dismiss / --dur-dismiss via CSS
 *   transform transition. onRevertEnd is wired to onTransitionEnd.
 *   prefers-reduced-motion: revert is skipped at the hook level (hook goes
 *   straight to IDLE), so the ghost never reaches the reverting render path.
 *
 * Reduced-motion (§5.3): Ghost still moves during active drag — it's direct
 * manipulation, not decoration. The transition easing on shadow/scale is
 * suppressed, but the ghost position tracks the pointer unconditionally.
 *
 * Immutability: functional component, renders from props only.
 */
import { useEffect, useRef } from 'react';
import type { DragMoveState } from '../edit-mode/use-drag-move.js';
import type { DragResizeState } from '../edit-mode/use-drag-resize.js';
import { sizeToWH } from 'briefpane/schema';
import type { Board } from 'briefpane/schema';

interface DragGhostProps {
  /** Current drag state — null dragTileId means no drag active. */
  readonly dragState: DragMoveState;
  /** The tile label or type string to display in the ghost. */
  readonly label: string;
  /** Grid cell width in pixels (including gap) — used to compute ghost size. */
  readonly cellWidth: number;
  /** Grid cell height in pixels (including gap) — used to compute ghost size. */
  readonly cellHeight: number;
  /** Pixel offset from grid container top-left to current pointer position. */
  readonly pointerX: number;
  readonly pointerY: number;
  /**
   * Tile footprint in grid columns (from layout.w). Defaults to 1.
   * The ghost is sized to the tile's ACTUAL footprint rather than one cell.
   */
  readonly tileW?: number;
  /**
   * Tile footprint in grid rows (from layout.h). Defaults to 1.
   */
  readonly tileH?: number;
  /**
   * Called when the revert transition ends (transitionend event).
   * Only relevant when dragState.phase === 'reverting'.
   */
  readonly onRevertEnd?: () => void;
}

/**
 * Renders a floating ghost card at the current pointer position during drag,
 * or animating back to origin during the spring-revert phase.
 *
 * Active during phase === 'dragging' (follows pointer) and
 * phase === 'reverting' (springs back to origin cell).
 */
export function DragGhost({
  dragState,
  label,
  cellWidth,
  cellHeight,
  pointerX,
  pointerY,
  tileW = 1,
  tileH = 1,
  onRevertEnd,
}: DragGhostProps): React.ReactElement | null {
  const ghostRef = useRef<HTMLDivElement>(null);

  // Ghost sized to the tile's actual footprint (w×h cells), centred on pointer.
  // ghostWidth = w columns × cellWidth − gap (the gap is only between tiles,
  // not at the right edge of the last column, but subtracting gap once gives
  // a close-enough approximation that matches the grid visually).
  const ghostWidth = tileW * cellWidth - 12;
  const ghostHeight = tileH * cellHeight - 12;

  // Spring-revert animation: on mount with phase === 'reverting', set the ghost
  // at revertFromX/Y instantly (no transition), then on the next frame set a
  // CSS transition and translate to revertToX/Y. This triggers the spring.
  //
  // We use a two-rAF trick to ensure the browser paints the initial position
  // before the transition is applied (otherwise the browser may batch both
  // style changes and skip the animation).
  useEffect(() => {
    if (dragState.phase !== 'reverting') return;
    const el = ghostRef.current;
    if (!el) return;
    if (
      dragState.revertFromX === null ||
      dragState.revertFromY === null ||
      dragState.revertToX === null ||
      dragState.revertToY === null
    ) return;

    const fromX = dragState.revertFromX;
    const fromY = dragState.revertFromY;
    const toX = dragState.revertToX;
    const toY = dragState.revertToY;

    // Step 1: position the ghost at the release point instantly (no transition).
    // The ghost's CSS `left/top` is set to toX/Y (the origin cell centre), so we
    // translate FROM (fromX - toX, fromY - toY) TO (0, 0).
    const dx = fromX - toX;
    const dy = fromY - toY;
    el.style.transition = 'none';
    el.style.transform = `scale(1.02) translate(${dx}px, ${dy}px)`;

    // Step 2: on the next two frames, enable transition and animate to origin.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        el.style.transition = `transform var(--dur-dismiss) var(--spring-dismiss)`;
        el.style.transform = 'scale(1) translate(0, 0)';
      });
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [
    dragState.phase,
    dragState.revertFromX,
    dragState.revertFromY,
    dragState.revertToX,
    dragState.revertToY,
  ]);

  if (dragState.phase === 'dragging' && dragState.dragTileId !== null) {
    return (
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          // Centre the ghost on the pointer.
          left: pointerX - ghostWidth / 2,
          top: pointerY - ghostHeight / 2,
          width: ghostWidth,
          height: ghostHeight,
          pointerEvents: 'none',
          zIndex: 100,
          /*
           * Calm ghost: low opacity, accent border, no shadow per design.
           * glance-grammar-spec §5.2: no bounce or elastic easing.
           * Size matches actual tile footprint (w×h cells) — see tileW/tileH props.
           */
          opacity: 0.5,
          backgroundColor: 'var(--color-bg-tile)',
          border: '2px solid var(--color-accent)',
          borderRadius: 'var(--radius-tile)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--text-label)',
          color: 'var(--color-text-dim)',
          fontWeight: 500,
          userSelect: 'none',
          /*
           * Scale-up per spec (1.02×). Transition suppressed by prefers-reduced-motion
           * guard in index.css — see tile-drag-ghost transition rule below.
           */
          transform: 'scale(1.02)',
        }}
      >
        {label}
      </div>
    );
  }

  if (dragState.phase === 'reverting' && dragState.dragTileId !== null) {
    // Ghost is positioned at the origin cell centre (revertToX/Y) —
    // the useEffect above immediately offsets it to revertFromX/Y via transform,
    // then animates back to translate(0,0) using --spring-dismiss.
    const centreX = dragState.revertToX ?? 0;
    const centreY = dragState.revertToY ?? 0;
    return (
      <div
        ref={ghostRef}
        aria-hidden="true"
        onTransitionEnd={onRevertEnd}
        style={{
          position: 'absolute',
          left: centreX - ghostWidth / 2,
          top: centreY - ghostHeight / 2,
          width: ghostWidth,
          height: ghostHeight,
          pointerEvents: 'none',
          zIndex: 100,
          opacity: 0.5,
          backgroundColor: 'var(--color-bg-tile)',
          border: '2px solid var(--color-accent)',
          borderRadius: 'var(--radius-tile)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--text-label)',
          color: 'var(--color-text-dim)',
          fontWeight: 500,
          userSelect: 'none',
          // transform is set imperatively by the useEffect above.
        }}
      >
        {label}
      </div>
    );
  }

  return null;
}

/**
 * TargetCellOutline — a pulsing outline at the current ghost drop target cell.
 *
 * glance-grammar-spec §5.1 (Tile drop target indicator):
 *   "Empty slot highlight pulse (not continuous; 2 pulses then hold)".
 *   Applied as a CSS class; animation is in index.css.
 */
interface TargetCellOutlineProps {
  readonly dragState: DragMoveState;
  /** Grid container element — used to compute cell pixel position. */
  readonly containerRect: DOMRect | null;
  readonly cellWidth: number;
  readonly cellHeight: number;
}

export function TargetCellOutline({ dragState, containerRect, cellWidth, cellHeight }: TargetCellOutlineProps): React.ReactElement | null {
  if (!dragState.isDragging || !containerRect) return null;

  const left = dragState.ghostX * cellWidth;
  const top = dragState.ghostY * cellHeight;

  return (
    <div
      aria-hidden="true"
      className="tile-drop-target"
      style={{
        position: 'absolute',
        left,
        top,
        width: cellWidth - 12,
        height: cellHeight - 12,
        border: '2px dashed var(--color-accent)',
        borderRadius: 'var(--radius-tile)',
        pointerEvents: 'none',
        zIndex: 50,
        boxSizing: 'border-box',
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* ResizeOutline — live preview of the snapped resize footprint                */
/* -------------------------------------------------------------------------- */

interface ResizeOutlineProps {
  readonly resizeState: DragResizeState;
  readonly board: Board;
  readonly containerRect: DOMRect | null;
  readonly cellWidth: number;
  readonly cellHeight: number;
}

/**
 * ResizeOutline — shows a dashed outline at the snapped resize preview footprint.
 *
 * Shown while the user drags the SE-corner resize handle. The outline snaps to
 * the nearest allowed size and turns red when the resize would be invalid
 * (collision or out of bounds).
 *
 * Immutability: pure functional component.
 */
export function ResizeOutline({ resizeState, board, containerRect, cellWidth, cellHeight }: ResizeOutlineProps): React.ReactElement | null {
  if (!resizeState.isResizing || !resizeState.previewSize || !containerRect) return null;
  const tileId = resizeState.resizeTileId;
  if (!tileId) return null;
  const entry = board.layout.find((l) => l.tileId === tileId);
  if (!entry) return null;
  const { w, h } = sizeToWH(resizeState.previewSize);
  const left = entry.x * cellWidth;
  const top = entry.y * cellHeight;
  const width = w * cellWidth - 12;
  const height = h * cellHeight - 12;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left,
        top,
        width,
        height,
        border: `2px dashed ${resizeState.previewInvalid ? 'var(--color-negative-accent)' : 'var(--color-accent)'}`,
        borderRadius: 'var(--radius-tile)',
        pointerEvents: 'none',
        zIndex: 50,
        boxSizing: 'border-box',
        opacity: resizeState.previewInvalid ? 0.5 : 0.8,
        backgroundColor: resizeState.previewInvalid ? 'rgba(198,40,40,0.05)' : 'transparent',
      }}
    />
  );
}
